import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import LoginPage from './pages/Login';
import QueryPage from './pages/Query';
import DocumentsPage from './pages/Documents';
import AuditLogPage from './pages/AuditLog';
import FAQQueuePage from './pages/FAQQueue';
import DeptDashboardPage from './pages/DeptDashboard';
import AdminDashboardPage from './pages/AdminDashboard';
import ModelRoutingPage from './pages/ModelRouting';
import DocManagementPage from './pages/DocManagement';
import ROIReportPage from './pages/ROIReport';
import AdminAuditPage from './pages/AdminAudit';
import SSOCallbackPage from './pages/SSOCallback';
import AppShell from './components/AppShell';
import './index.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ─── Auth Context ───
const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const STORAGE_KEY = 'vedanta.auth';

// Apply the persisted auth header BEFORE any component mounts, so the very first
// request on a hard refresh (e.g. /api/cost-meter from the topbar) carries it.
(function applyPersistedAuthHeader() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (stored?.token) {
      axios.defaults.headers.common.Authorization = `Bearer ${stored.token}`;
    }
  } catch { /* ignore corrupt storage */ }
})();

function AuthProvider({ children }) {
  const [auth, setAuthState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); }
    catch { return null; }
  });
  const [lang, setLang] = useState(() => localStorage.getItem('vedanta.lang') || 'en');

  const setAuth = useCallback((v) => {
    if (v) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
      axios.defaults.headers.common.Authorization = `Bearer ${v.token}`;
    } else {
      localStorage.removeItem(STORAGE_KEY);
      delete axios.defaults.headers.common.Authorization;
    }
    setAuthState(v);
  }, []);

  const setLanguage = useCallback((l) => {
    localStorage.setItem('vedanta.lang', l);
    setLang(l);
  }, []);

  // Belt-and-suspenders: an interceptor also stamps the header. Helps if
  // some axios instance is created later that bypasses defaults.
  useEffect(() => {
    const id = axios.interceptors.request.use(cfg => {
      if (auth?.token && cfg.url?.startsWith(API) && !cfg.headers.Authorization) {
        cfg.headers.Authorization = `Bearer ${auth.token}`;
      }
      return cfg;
    });
    return () => axios.interceptors.request.eject(id);
  }, [auth]);

  // Auto-logout on 401
  useEffect(() => {
    const id = axios.interceptors.response.use(
      r => r,
      err => {
        if (err?.response?.status === 401 && auth) {
          setAuth(null);
          toast.error('Session expired. Please sign in again.');
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [auth, setAuth]);

  return (
    <AuthCtx.Provider value={{ auth, setAuth, lang, setLanguage }}>
      {children}
    </AuthCtx.Provider>
  );
}

function RequireAuth({ children, roles }) {
  const { auth } = useAuth();
  if (!auth) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(auth.user.role)) return <Navigate to="/" replace />;
  return children;
}

function Root() {
  const { auth } = useAuth();
  if (!auth) return <Navigate to="/login" replace />;
  // Route to default landing per role
  if (auth.user.role === 'super_admin') return <Navigate to="/admin/dashboard" replace />;
  if (auth.user.role === 'dept_head') return <Navigate to="/dept/dashboard" replace />;
  return <Navigate to="/ask" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/sso/callback" element={<SSOCallbackPage />} />
          <Route path="/" element={<Root />} />

          {/* All roles */}
          <Route path="/ask" element={<RequireAuth><AppShell><QueryPage /></AppShell></RequireAuth>} />
          <Route path="/documents" element={<RequireAuth><AppShell><DocumentsPage /></AppShell></RequireAuth>} />

          {/* Dept head + super admin */}
          <Route path="/dept/dashboard" element={<RequireAuth roles={['dept_head','super_admin']}><AppShell><DeptDashboardPage /></AppShell></RequireAuth>} />
          <Route path="/dept/faqs" element={<RequireAuth roles={['dept_head','super_admin']}><AppShell><FAQQueuePage /></AppShell></RequireAuth>} />
          <Route path="/dept/audit" element={<RequireAuth roles={['dept_head','super_admin']}><AppShell><AuditLogPage /></AppShell></RequireAuth>} />

          {/* Super admin */}
          <Route path="/admin/dashboard" element={<RequireAuth roles={['super_admin']}><AppShell><AdminDashboardPage /></AppShell></RequireAuth>} />
          <Route path="/admin/routing" element={<RequireAuth roles={['super_admin']}><AppShell><ModelRoutingPage /></AppShell></RequireAuth>} />
          <Route path="/admin/documents" element={<RequireAuth roles={['super_admin']}><AppShell><DocManagementPage /></AppShell></RequireAuth>} />
          <Route path="/admin/roi" element={<RequireAuth roles={['super_admin']}><AppShell><ROIReportPage /></AppShell></RequireAuth>} />
          <Route path="/admin/audit" element={<RequireAuth roles={['super_admin']}><AppShell><AdminAuditPage /></AppShell></RequireAuth>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

// Helper for use in pages
export function useNav() { return useNavigate(); }
